<template>
  <div class="settings-page">
    <h2>Task Bindings</h2>
    <div class="subtitle">
      Configure which databases contribute tasks to your agenda.
    </div>

    <section>
      <h3>Personal bindings</h3>
      <BaseAlert
        v-if="!personalBindings.length && !workspaceBindings.length"
        state="info"
      >
        No task bindings configured.
      </BaseAlert>
      <div v-for="[id, binding] in personalBindings" :key="id" class="entry">
        <div class="binding-info">
          <BaseTypeIcon :image="databaseIcon(binding)" size="xs" plain />
          <div class="binding-labels">
            <span class="db-name">{{ databaseName(binding) }}</span>
            <span class="ws-name">{{ wsName(binding.workspace_id) }}</span>
          </div>
        </div>
        <div class="binding-actions">
          <BaseButton
            kind="auxiliary"
            :text="binding.filter ? 'Filtered' : 'No filter'"
            @click="() => openEditFilter(id, binding)"
          >
            <template #icon>
              <FilterIcon />
            </template>
          </BaseButton>
          <BaseButton
            kind="auxiliary"
            :text="binding.status_mapping ? 'Status mapped' : 'No status'"
            @click="() => openEditStatus(id, binding)"
          />
          <BaseButton
            kind="auxiliary"
            :text="
              binding.scheduled_mapping ? 'Scheduled mapped' : 'No schedule'
            "
            @click="() => openEditScheduled(id, binding)"
          />
          <BaseButton
            kind="auxiliary"
            :text="binding.url_mapping ? 'URL mapped' : 'No URL'"
            @click="() => openEditUrl(id, binding)"
          />
          <BaseButton
            kind="main"
            type="secondary"
            tint="red"
            text="Remove"
            @click="() => removeBinding(id)"
          />
        </div>
      </div>
    </section>

    <section>
      <h3>Add personal binding</h3>
      <BaseAlert state="info">
        Select a database whose rows should appear as tasks in your agenda. Each
        binding can be filtered and mapped to Routine's task status and schedule
        fields.
      </BaseAlert>
      <div class="add-form">
        <div class="org-ws-row">
          <BaseInputSelect
            :value="selectedOrgId"
            :options="orgOptions"
            placeholder="Select an org…"
            :select-only="true"
            :searchable="true"
            :disabled="!orgOptions.length"
            @update:value="onOrgSelected"
          />
          <BaseInputSelect
            :value="selectedWorkspaceId"
            :options="workspaceOptions"
            placeholder="Select a workspace…"
            :select-only="true"
            :searchable="true"
            :disabled="!selectedOrgId"
            @update:value="onWorkspaceSelected"
          />
        </div>
        <template v-if="selectedOrgId && selectedWorkspaceId">
          <BaseInputSelect
            :value="selectedTableId"
            :options="databaseOptions"
            placeholder="Select a database…"
            :select-only="true"
            :searchable="true"
            @update:value="onDatabaseSelected"
          >
            <template #selected-icon="{ value }">
              <BaseTypeIcon :image="dbIconMap[value]" size="xs" plain />
            </template>
            <template #item="{ option }">
              <BaseInputSelectOptionItem :title="option.label">
                <template #icon>
                  <BaseTypeIcon
                    :image="dbIconMap[option.value]"
                    size="xs"
                    plain
                  />
                </template>
              </BaseInputSelectOptionItem>
            </template>
          </BaseInputSelect>
          <BaseButton
            kind="main"
            type="primary"
            text="Add"
            :state="canAdd ? 'default' : 'disabled'"
            @click="addBinding"
          />
        </template>
      </div>
    </section>

    <section v-if="workspaceBindings.length">
      <h3>Workspaces bindings</h3>
      <BaseAlert state="info">
        Task bindings coming from workspaces that can only be configured there.
        You can disable them locally.
      </BaseAlert>
      <div
        v-for="{ id, binding, workspaceId } in workspaceBindings"
        :key="id"
        class="entry workspace-entry"
        role="button"
        tabindex="0"
        @click="() => openWorkspaceBinding(workspaceId)"
      >
        <div class="binding-info">
          <BaseTypeIcon :image="databaseIcon(binding)" size="xs" plain />
          <div class="binding-labels">
            <span class="db-name">{{ databaseName(binding) }}</span>
            <span class="ws-name">{{ wsName(workspaceId) }}</span>
          </div>
        </div>
        <div class="binding-actions" @click.stop>
          <BaseToggleButton
            :checked="isEnabled(id)"
            @change="(enabled) => setEnabled(id, enabled)"
          />
        </div>
      </div>
    </section>

    <VueFinalModal
      v-model="editingOpen"
      :focus-trap="modalFocusTrap"
      class="final-modal"
      :class="{
        'theme--default': theme === 'light',
        'theme--dark': theme === 'dark',
      }"
      overlay-transition="vfm-fade"
      @closed="closeEditing"
    >
      <TaskBindingEditor
        v-if="editingId !== null && editingBinding && editingDatabase"
        :binding-id="editingId"
        :binding="editingBinding"
        :database="editingDatabase"
        @close="editingOpen = false"
      />
    </VueFinalModal>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type Component } from "vue";
import { VueFinalModal } from "vue-final-modal";

import type {
  GraphDatabase,
  OrgId,
  TaskBinding,
  TaskBindingDictionaryDictionary,
  TaskBindingSettingsDictionary,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import BaseInputSelectOptionItem from "@/components/base/BaseInputSelectOptionItem.vue";
import BaseToggleButton from "@/components/base/BaseToggleButton.vue";
import BaseTypeIcon from "@/components/base/BaseTypeIcon.vue";
import { getTypeIcon } from "@/components/databases";
import FilterIcon from "@/components/icons/FilterIcon.vue";
import * as inject from "@/components/inject";
import { modalFocusTrap } from "@/components/modals/focus-trap";
import TaskBindingEditor from "@/components/settings/TaskBindingEditor.vue";
import { useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";

export default defineComponent({
  components: {
    BaseButton,
    BaseTypeIcon,
    BaseInputSelect,
    BaseInputSelectOptionItem,
    BaseAlert,
    BaseToggleButton,
    FilterIcon,
    TaskBindingEditor,
    VueFinalModal,
  },
  async setup() {
    const controller = inject.controller();
    const workspaceDatabases = inject.workspaceDatabases();
    const profile = inject.profile();
    const router = inject.router();
    const theme = inject.theme();

    const releaser = useReleaser();

    const [bindings, bindingsByWorkspace, bindingSettings] = await Promise.all([
      releaser.add(controller.task.binding.all()),
      releaser.add(controller.task.binding.byWorkspace()),
      releaser.add(controller.task.binding.settings()),
    ]);
    const personalWorkspaceId = profile.value.user.personal.workspaceId;

    // Load workspace names once
    const wsIdsRef = await controller.workspace.all();
    const wsIds = wsIdsRef.release() ?? [];
    const wsNameEntries = await Promise.all(
      wsIds.map(async (id: WorkspaceId): Promise<[string, string]> => {
        const ref = await controller.workspace.get(id);
        return [id, ref.release()?.name ?? id];
      })
    );
    const workspaceNameMap = Object.fromEntries(wsNameEntries);

    // Load orgs and their workspace membership
    const orgIdsRef = await controller.organization.all();
    const orgIds = orgIdsRef.release() ?? [];
    const orgInfoEntries = await Promise.all(
      orgIds.map(async (id: OrgId): Promise<[string, string]> => {
        const ref = await controller.organization.get(id);
        return [id, ref.release()?.name ?? id];
      })
    );
    const orgNameMap = Object.fromEntries(orgInfoEntries);
    const orgWorkspacesEntries = await Promise.all(
      orgIds.map(async (id: OrgId): Promise<[OrgId, WorkspaceId[]]> => {
        const ref = await controller.organization.workspaces.all(id);
        return [id, ref.release() ?? []];
      })
    );
    const orgWorkspaceMap = Object.fromEntries(orgWorkspacesEntries);

    // Reverse lookup workspace id -> organization id, to navigate workspace
    // bindings to their organization settings page.
    const workspaceOrgMap: Record<string, string> = {};
    for (const [orgId, wsList] of Object.entries(orgWorkspaceMap)) {
      for (const wsId of wsList) workspaceOrgMap[wsId] = orgId;
    }

    // Editing state: the editor stays mounted until the modal has faded out.
    const editingOpen = ref(false);
    const editingId = ref<string | null>(null);
    const editingDatabase = ref<GraphDatabase | null>(null);
    const editingBinding = computed<TaskBinding | null>(() => {
      const id = editingId.value;
      return id ? (bindings.value?.[id] ?? null) : null;
    });

    // Database picker state
    const selectedOrgId = ref("");
    const selectedWorkspaceId = ref("");
    const selectedTableId = ref("");

    const orgOptions = computed<SelectOption[]>(() =>
      orgIds.map((id) => ({
        value: id,
        label: orgNameMap[id] ?? id,
        aliases: [],
      }))
    );

    const workspaceOptions = computed<SelectOption[]>(() => {
      const wsIds = selectedOrgId.value
        ? (orgWorkspaceMap[selectedOrgId.value] ?? [])
        : [];
      return wsIds.map((id) => ({
        value: id,
        label: workspaceNameMap[id] ?? id,
        aliases: [],
      }));
    });

    const databaseOptions = computed<SelectOption[]>(() => {
      const dbs = selectedWorkspaceId.value
        ? Object.values(
            workspaceDatabases.value?.[selectedWorkspaceId.value] ?? {}
          )
        : [];
      return dbs
        .sort((a, b) => a.data_name.localeCompare(b.data_name))
        .map((db) => ({ value: db.id, label: db.data_name, aliases: [] }));
    });

    const dbIconMap = computed<Record<string, Component | string>>(() => {
      const result: Record<string, Component | string> = {};
      const wsDbs = selectedWorkspaceId.value
        ? workspaceDatabases.value?.[selectedWorkspaceId.value]
        : null;
      if (wsDbs) {
        for (const db of Object.values(wsDbs)) {
          result[db.id] = getTypeIcon(profile.value, db);
        }
      }
      return result;
    });

    const canAdd = computed(() => !!selectedTableId.value);

    // Personal bindings (stored in the personal workspace) get the full
    // configuration UI.
    const personalBindings = computed<[string, TaskBinding][]>(() => {
      const dict: TaskBindingDictionaryDictionary =
        bindingsByWorkspace.value ?? {};
      return Object.entries(dict[personalWorkspaceId] ?? {});
    });

    // Bindings coming from other workspaces can only be enabled/disabled here.
    const workspaceBindings = computed<
      { id: string; binding: TaskBinding; workspaceId: string }[]
    >(() => {
      const dict: TaskBindingDictionaryDictionary =
        bindingsByWorkspace.value ?? {};
      const result: {
        id: string;
        binding: TaskBinding;
        workspaceId: string;
      }[] = [];
      for (const [workspaceId, wsBindings] of Object.entries(dict)) {
        if (workspaceId === personalWorkspaceId) continue;
        for (const [id, binding] of Object.entries(wsBindings)) {
          result.push({ id, binding, workspaceId });
        }
      }
      return result;
    });

    const isEnabled = (id: string) => {
      const dict: TaskBindingSettingsDictionary = bindingSettings.value ?? {};
      return dict[id]?.enabled ?? true;
    };

    const setEnabled = async (id: string, enabled: boolean) => {
      await controller.task.binding.setEnabled(id, enabled);
    };

    const openWorkspaceBinding = (workspaceId: string) => {
      const orgId = workspaceOrgMap[workspaceId];
      if (!orgId) return;
      navigateTo(RouteNames.SettingsWorkspaceTaskBindings, router, {
        params: { orgId, workspaceId },
      });
    };

    const dbFor = (binding: TaskBinding) =>
      workspaceDatabases.value?.[binding.workspace_id]?.[binding.table_id] ??
      null;

    const databaseName = (binding: TaskBinding) =>
      dbFor(binding)?.data_name ?? binding.table_id;

    const databaseIcon = (binding: TaskBinding): Component | string =>
      getTypeIcon(profile.value, dbFor(binding));

    const wsName = (workspaceId: string) =>
      workspaceNameMap[workspaceId] ?? workspaceId;

    const onOrgSelected = (orgId: string) => {
      selectedOrgId.value = orgId;
      selectedWorkspaceId.value = "";
      selectedTableId.value = "";
    };

    const onWorkspaceSelected = (wsId: string) => {
      selectedWorkspaceId.value = wsId;
      selectedTableId.value = "";
    };

    const onDatabaseSelected = (tableId: string) => {
      selectedTableId.value = tableId;
    };

    const addBinding = async () => {
      if (!selectedWorkspaceId.value || !selectedTableId.value) return;
      await controller.task.binding.add(
        selectedWorkspaceId.value,
        selectedTableId.value,
        null
      );
      selectedOrgId.value = "";
      selectedWorkspaceId.value = "";
      selectedTableId.value = "";
    };

    const removeBinding = async (id: string) => {
      await controller.task.binding.remove(id);
    };

    const openEditing = (id: string, binding: TaskBinding) => {
      editingId.value = id;
      editingDatabase.value = dbFor(binding);
      editingOpen.value = true;
    };
    const openEditFilter = openEditing;
    const openEditStatus = openEditing;
    const openEditScheduled = openEditing;
    const openEditUrl = openEditing;

    const closeEditing = () => {
      editingId.value = null;
      editingDatabase.value = null;
    };

    return {
      modalFocusTrap,
      personalBindings,
      workspaceBindings,
      isEnabled,
      setEnabled,
      openWorkspaceBinding,
      selectedOrgId,
      selectedWorkspaceId,
      selectedTableId,
      orgOptions,
      workspaceOptions,
      databaseOptions,
      dbIconMap,
      canAdd,
      databaseName,
      databaseIcon,
      wsName,
      onOrgSelected,
      onWorkspaceSelected,
      onDatabaseSelected,
      addBinding,
      removeBinding,
      theme,
      editingOpen,
      editingId,
      editingDatabase,
      editingBinding,
      openEditFilter,
      openEditStatus,
      openEditScheduled,
      openEditUrl,
      closeEditing,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.entry {
  .binding-info {
    display: flex;
    align-items: center;
    gap: 8px;

    .binding-labels {
      display: flex;
      flex-direction: column;

      .db-name {
        @extend %font-body-medium;
      }

      .ws-name {
        @extend %font-small-regular;
        @include themed() {
          color: t($grey-50);
        }
      }
    }
  }

  .binding-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.workspace-entry {
  cursor: pointer;
  border-radius: $border-radius-m;
  padding: 4px;

  &:hover {
    @include themed() {
      background-color: t($grey-05);
    }
  }
}

.add-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;

  .org-ws-row {
    display: flex;
    gap: 8px;

    > * {
      flex: 1;
      min-width: 0;
    }
  }
}
</style>
